import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { GlobalSwal } from '../../styles/swal.style';
import { ApiDigitalAdminContact } from '../../services/admin/contact.service';
import { Error } from '../../models/error.model';
import { Company, CompanyToken } from '../../models/company.model';
import { Contact } from '../../models/contact.model';
import { legendIconValid, legendValidInvalidRestart, legendIconValidInvalidRestart } from '../../utils/legend.data.util';
import { modalHide, modalShow } from '../../utils/modal.util';
import { uploadTooltip } from '../../utils/tooltip.util';
import { uploadUrl } from '../../scripts/file.load.script';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { getError } from '../../languages/translations/response';
import { resolveCanton } from '../../data/cantons';

import AppModalContactUpdateExtend from './ModalContactUpdateExtend';
import AppLoading from '../Loading';
import AppContactFormAccordion from '../../interfaces/main/contact/component/ContactFormAccordion';

export interface AppModalContactUpdateProps {
  companyForUser: CompanyToken | null,
  meCompanies? : Company[] | null,
  contact: Contact | null,
  contacts?: Contact[] | null,
  contactCompanies: Company[] | null,
  setContact: Dispatch<SetStateAction<Contact | null>>,
  setContacts?: Dispatch<SetStateAction<Contact[] | null>>,
  setContactCompanies?: Dispatch<SetStateAction<Company[] | null>>,
  loadAllContacts: Function,
  loadContactsForCompany?: Function
};

let errorResponse: Error, contactResponse: Contact;

const AppModalContactUpdate: React.FunctionComponent<AppModalContactUpdateProps> = ({companyForUser, meCompanies, contact, contacts, contactCompanies, setContact, setContactCompanies, setContacts, loadAllContacts, loadContactsForCompany}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [name, setName] = useState({value: '', valid: false})
  const [firstName, setFirstName] = useState({value: '', valid: false})
  const [lastName, setLastName] = useState({value: '', valid: false})
  const [logo, setLogo] = useState({value: {name: '', size: 0, type: '', view: '', file: null as any}, valid: {file: false, message: ''}})
  const [email, setEmail] = useState({value: '', valid: false})
  const [phone, setPhone] = useState({value: '', valid: false})
  const [website, setWebsite] = useState({value: '', valid: false})
  const [vatNumber, setVatNumber] = useState({value: '', valid: false})
  const [registerNumber, setRegisterNumber] = useState({value: '', valid: false})
  const [street, setStreet] = useState({value: '', valid: false})
  const [streetNumber, setStreetNumber] = useState({value: '', valid: false})
  const [additional, setAdditional] = useState({value: '', valid: false})
  const [administrativeArea, setAdministrativeArea] = useState({value: '', valid: false})
  const [city, setCity] = useState({value: '', valid: false})
  const [zipCode, setZipCode] = useState({value: '', valid: false})
  const [country, setCountry] = useState({value: '', valid: false})
  const [currency, setCurrency] = useState({value: '', valid: false, object: null as any})
  const [financialEntity, setFinancialEntity] = useState({value: '', valid: false})
  const [iban, setIban] = useState({value: '', valid: false})
  const [bban, setBban] = useState({value: '', valid: false})
  const [bic, setBic] = useState({value: '', valid: false})
  const [language, setLanguage] = useState({value: '', valid: false})
  const [qrType, setQrType] = useState({value: '', valid: false})
  const [values, setValues] = useState({name: '', first_name: '', last_name: '', logo: null, email: '', phone: '', website: '', register_number: '', vat_number: '', street: '', street_number: '', additional: '', administrative_area: '', city: '', zip_code: '', country: '', currency: '', name: '', iban: '', bban: '', bic: '', language: '', qr_type: ''})

  const submitContactUpdate = async () => {
    setLoadIndicator('on')

    if (companyForUser && contact && name.valid && firstName.valid && lastName.valid && logo.valid.file && email.valid && phone.valid && website.valid && vatNumber.valid && registerNumber.valid && street.valid && streetNumber.valid && additional.valid && administrativeArea.valid && city.valid && zipCode.valid && country.valid && currency.valid && financialEntity.valid && iban.valid && bban.valid && bic.valid && language.valid && qrType.valid) {
      let addressStreetNumber = (streetNumber.value.length === 0) ? 'NN' : streetNumber.value
      let valueIban = iban.value.toUpperCase().trim().replaceAll(' ', '')
      let valueBban = bban.value.toUpperCase().trim().replaceAll(' ', '')
      let valueBic = bic.value.toUpperCase().trim().replaceAll(' ', '')

      let cantonForBackend = administrativeArea.value;

      await ApiDigitalAdminContact.putContact(companyForUser.id, contact.id, contact.type, name.value, firstName.value, lastName.value, logo.value.file, email.value, phone.value, website.value, registerNumber.value, vatNumber.value, street.value, addressStreetNumber, additional.value, cantonForBackend, city.value, zipCode.value, country.value, currency.value, financialEntity.value, valueIban, valueBban, valueBic, language.value, qrType.value, []).then( (response) => {
        if (response.status === 200) {
          contactResponse = response.data
          loadAllContacts(companyForUser.id)
          if (loadContactsForCompany) loadContactsForCompany(companyForUser.id)

          if (contacts && setContacts) {
            setContacts(contacts.map((item) => ((item.id === contact.id) ? contactResponse : item)))
          }
          if (meCompanies && setContactCompanies) {
            setContact(contactResponse)
            setContactCompanies(meCompanies.filter((item) => (contactResponse.data.companies.includes(item.id))))
          }

          GlobalSwal.fire({
            title: lang.labels.successfullyUpdatedContact,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalContactUpdate()
          })
        } else {
          errorResponse = response.data

          const errorTitle = errorResponse.code === 'VALIDATION_ERROR' && errorResponse.message?.toLowerCase().includes('type mismatch')
            ? lang.labels.uuidTypeMismatch
            : getError(errorResponse.code, lang.code, errorResponse.message)

          GlobalSwal.fire({
            title: errorTitle,
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt,
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        setLoadIndicator('off')
      })
    }
  }

  const executeShowModalContactUpdateExtend = () => {
    if (companyForUser && contact && name.valid && firstName.valid && lastName.valid && logo.valid.file && email.valid && phone.valid && website.valid && vatNumber.valid && registerNumber.valid && street.valid && streetNumber.valid && additional.valid && administrativeArea.valid && city.valid && zipCode.valid && country.valid && currency.valid && financialEntity.valid && iban.valid && bban.valid && bic.valid && language.valid && qrType.valid) {
      let cantonExtend = administrativeArea.value;
      setValues({name: name.value, first_name: firstName.value, last_name: lastName.value, logo: logo.value.file, email: email.value, phone: phone.value, website: website.value, register_number: registerNumber.value, vat_number: vatNumber.value, street: street.value, street_number: streetNumber.value, additional: additional.value, administrative_area: cantonExtend, city: city.value, zip_code: zipCode.value, country: country.value, currency: currency.value, name: financialEntity.value, iban: iban.value, bban: bban.value, bic: bic.value, language: language.value, qr_type: qrType.value})
      modalHide('modal-contact-update')
      modalShow('modal-contact-update-extend')
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        confirmButtonText: lang.labels.okGotIt
      }).then( () => {
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModalContactUpdate = () => {
    modalHide('modal-contact-update')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendIconValidInvalidRestart('modal-contact-update-input-name', 'modal-contact-update-legend-name-valid')
    legendIconValidInvalidRestart('modal-contact-update-input-name', 'modal-contact-update-legend-name-required')
    legendIconValidInvalidRestart('modal-contact-update-input-first-name', 'modal-contact-update-legend-first-name-valid')
    legendIconValidInvalidRestart('modal-contact-update-input-first-name', 'modal-contact-update-legend-first-name-required')
    legendIconValidInvalidRestart('modal-contact-update-input-last-name', 'modal-contact-update-legend-last-name-valid')
    legendIconValidInvalidRestart('modal-contact-update-input-last-name', 'modal-contact-update-legend-last-name-required')
    legendIconValidInvalidRestart('modal-contact-update-input-email', 'modal-contact-update-legend-email-valid')
    legendIconValidInvalidRestart('modal-contact-update-input-phone', 'modal-contact-update-legend-phone-valid')
    legendIconValidInvalidRestart('modal-contact-update-input-website', 'modal-contact-update-legend-website-valid')
    legendIconValidInvalidRestart('modal-contact-update-input-register-number', 'modal-contact-update-legend-register-number-valid')
    legendIconValidInvalidRestart('modal-contact-update-input-vat-number', 'modal-contact-update-legend-vat-number-valid')
    legendIconValidInvalidRestart('modal-contact-update-input-street', 'modal-contact-update-legend-street-valid')
    legendIconValidInvalidRestart('modal-contact-update-input-street-number', 'modal-contact-update-legend-street-number-valid')
    legendIconValidInvalidRestart('modal-contact-update-input-additional', 'modal-contact-update-legend-additional-valid')
    legendIconValidInvalidRestart('modal-contact-update-input-administrative-area', 'modal-contact-update-legend-administrative-area-valid')
    legendIconValidInvalidRestart('modal-contact-update-input-city', 'modal-contact-update-legend-city-valid')
    legendIconValidInvalidRestart('modal-contact-update-input-zip-code', 'modal-contact-update-legend-zip-code-valid')
    legendValidInvalidRestart('modal-contact-update-legend-financial-entity-valid')
    legendValidInvalidRestart('modal-contact-update-legend-iban-valid')
    legendValidInvalidRestart('modal-contact-update-legend-bban-valid')
    legendValidInvalidRestart('modal-contact-update-legend-bic-valid')
    legendValidInvalidRestart('modal-contact-update-legend-country-valid')
    legendValidInvalidRestart('modal-contact-update-legend-currency-valid')
    legendValidInvalidRestart('modal-contact-update-legend-language-valid')
    legendValidInvalidRestart('modal-contact-update-legend-qr-type-valid')

    if (contact && contact.data.uuid.length > 0) {
      if (contact.type === 'person') {
        legendIconValid('modal-contact-update-input-first-name', 'modal-contact-update-legend-first-name-valid')
        legendIconValid('modal-contact-update-input-last-name', 'modal-contact-update-legend-last-name-valid')
        legendIconValid('modal-contact-update-input-email', 'modal-contact-update-legend-email-valid')
      }
      if (contact.type === 'company') {
        legendIconValid('modal-contact-update-input-name', 'modal-contact-update-legend-name-valid')
      }
    }

    if (contacts && setContacts) {
      setContact(null)
    }
  }

  useEffect( () => {
    if (contact) {
      if (contact.type === 'person') {
        setFirstName({value: contact.data.first_name, valid: true})
        setLastName({value: contact.data.last_name, valid: true})
        setName({value: '', valid: true})
        setLogo({value: {name: '', size: 0, type: '', view: '', file: null as any}, valid: {file: true, message: ''}})
        setVatNumber({value: '', valid: true})
        setRegisterNumber({value: '', valid: true})
        setCurrency({value: '', object: null, valid: true})
      }
      if (contact.type === 'company') {
        setName({value: contact.data.name, valid: true})
        setLogo(uploadUrl(lang, contact.data.logo))
        setVatNumber({value: contact.data.vat_number, valid: true})
        setRegisterNumber({value: contact.data.register_number, valid: true})
        setCurrency({value: contact.data.currency?.code ?? '', object: contact.data.currency ?? null, valid: true})
        setFirstName({value: '', valid: true})
        setLastName({value: '', valid: true})
      }

      setEmail({value: contact.data.email ?? '', valid: true})
      setPhone({value: contact.data.phone ?? '', valid: true})
      setWebsite({value: contact.data.website ?? '', valid: true})

      const addr = contact.data.address ?? ({} as any)
      setStreet({value: addr.street ?? '', valid: true})
      setStreetNumber({value: addr.number ?? '', valid: true})
      setAdditional({value: addr.additional ?? '', valid: true})
      const rawAdmin = addr.administrative_area ?? '';
      const resolvedAdmin = resolveCanton(rawAdmin);
      setAdministrativeArea({value: resolvedAdmin ? resolvedAdmin.code : rawAdmin, valid: true})
      setCity({value: addr.city ?? '', valid: true})
      setZipCode({value: addr.zip_code ?? '', valid: true})
      setCountry({value: addr.country ?? '', valid: true})

      const bank = contact.data.bank_account ?? ({} as any)
      setFinancialEntity({value: bank.name ?? '', valid: true})
      setIban({value: bank.iban ?? '', valid: true})
      setBban({value: bank.bban ?? '', valid: true})
      setBic({value: bank.bic ?? '', valid: true})
      setLanguage({value: contact.data.language ?? '', valid: true})
      setQrType({value: contact.data.qr_type ?? '', valid: true})
    } else {
      setName({value: '', valid: false})
      setFirstName({value: '', valid: false})
      setLogo({value: {name: '', size: 0, type: '', view: '', file: null as any}, valid: {file: false, message: ''}})
      setLastName({value: '', valid: false})
      setEmail({value: '', valid: false})
      setPhone({value: '', valid: false})
      setWebsite({value: '', valid: false})
      setVatNumber({value: '', valid: false})
      setRegisterNumber({value: '', valid: false})
      setStreet({value: '', valid: false})
      setStreetNumber({value: '', valid: false})
      setAdditional({value: '', valid: false})
      setAdministrativeArea({value: '', valid: false})
      setCity({value: '', valid: false})
      setZipCode({value: '', valid: false})
      setCountry({value: '', valid: false})
      setFinancialEntity({value: '', valid: false})
      setIban({value: '', valid: false})
      setBban({value: '', valid: false})
      setBic({value: '', valid: false})
      setCurrency({value: '', valid: false, object: null})
      setLanguage({value: '', valid: false})
      setQrType({value: '', valid: false})
    }

    uploadTooltip()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact])

  return (
    <>
      <div id="modal-contact-update" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="text-capitalize">{lang.labels.updateContactData}</h2>
            </div>
            <div className="modal-body p-0">
              { contact
                ?
                <AppContactFormAccordion contact={contact} name={name} firstName={firstName} lastName={lastName} logo={logo} email={email} phone={phone} website={website} vatNumber={vatNumber} registerNumber={registerNumber} street={street} streetNumber={streetNumber} additional={additional} administrativeArea={administrativeArea} city={city} zipCode={zipCode} country={country} currency={currency} financialEntity={financialEntity} iban={iban} bban={bban} bic={bic} language={language} qrType={qrType} setName={setName} setFirstName={setFirstName} setLastName={setLastName} setLogo={setLogo} setEmail={setEmail} setPhone={setPhone} setWebsite={setWebsite} setVatNumber={setVatNumber} setRegisterNumber={setRegisterNumber} setStreet={setStreet} setStreetNumber={setStreetNumber} setAdditional={setAdditional} setAdministrativeArea={setAdministrativeArea} setCity={setCity} setZipCode={setZipCode} setCountry={setCountry} setCurrency={setCurrency} setFinancialEntity={setFinancialEntity} setIban={setIban} setBban={setBban} setBic={setBic} setLanguage={setLanguage} setQrType={setQrType} ></AppContactFormAccordion>
                :
                <AppLoading></AppLoading>
              }
            </div>
            <div className="modal-footer flex-center">
              <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalContactUpdate}>{lang.labels.discard}</button>
              { contact && contact.data.companies.length === 1
                ?
                <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitContactUpdate}>
                  <span className="indicator-label">{lang.labels.update}</span>
                  <span className="indicator-progress">
                    {lang.labels.pleaseWait}
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                </button>
                :
                <button className="btn btn-primary mx-2" type="button" onClick={executeShowModalContactUpdateExtend}>{lang.labels.extendChanges}</button>
              }
            </div>
          </div>
        </div>
      </div>
      <AppModalContactUpdateExtend companyForUser={companyForUser} meCompanies={meCompanies} contact={contact} contacts={contacts} contactCompanies={contactCompanies} values={values} setContact={setContact} setContacts={setContacts} setContactCompanies={setContactCompanies} loadAllContacts={loadAllContacts} loadContactsForCompany={loadContactsForCompany} restartModalMain={restartModal}></AppModalContactUpdateExtend>
    </>
  )
};

export default AppModalContactUpdate;
